import React from 'react';
import { ArrowRight } from 'lucide-react';
import BookAssessment from './BookAssessment';

// What the assessment actually returns. Deliverable in thirty minutes, which is
// the test every line here has to pass: the closing CTA already promises "an
// honest read", and the hero is not allowed to promise more than the footer.
const outcomes = [
  'Whether an agent can finish it end to end',
  'What building it would actually involve',
  'An honest no, where that is the answer',
];

// In-page jump that still ships a real href, so the link survives prerendering
// and means something to a crawler; the handler only softens the motion.
const jumpTo = (id: string) => (event: React.MouseEvent<HTMLAnchorElement>) => {
  const target = document.getElementById(id);
  if (!target) return;
  event.preventDefault();
  target.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const HeroSection: React.FC = () => (
  <div id="home" className="bg-gray-900 py-12 pt-20 md:pt-28">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col items-center justify-between md:flex-row">
        <div className="mb-10 text-center md:mb-0 md:w-1/2 md:pr-10 md:text-left">
          <h1 className="font-display text-display-md font-bold text-balance text-white sm:text-display-lg lg:text-display-xl">
            Bring us the work. We&rsquo;ll build the agent that does it.
          </h1>
          <p className="mt-4 font-display text-display-sm text-gray-400">
            Consulting and implementation, from first spec to production
          </p>
          <p className="mt-6 max-w-measure text-lg text-gray-300">
            We build AI agents that use your tools, read your documents, and carry multi-step work
            through to a finished result. Cloud-agnostic: they run where your systems already live.
          </p>

          <BookAssessment placement="hero" variant="hero" band="gray-900" className="mt-8" />

          <p className="mt-6">
            <a
              href="#services"
              onClick={jumpTo('services')}
              className="inline-flex min-h-[44px] items-center py-2.5 font-medium text-accent-400 transition-colors duration-150 hover:text-accent-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-400"
            >
              See what we build <ArrowRight className="ml-1 h-4 w-4" aria-hidden="true" />
            </a>
          </p>
        </div>

        <div className="flex w-full justify-center md:w-1/2 md:justify-end">
          <div className="animate-panel-in w-full max-w-md rounded-lg border border-gray-700 bg-gray-800 p-6 shadow-xl sm:p-7">
            <h2 className="font-display text-display-sm font-medium text-white">
              Start with one process
            </h2>
            <p className="mt-4 text-gray-300">
              Tell us about one thing your team does by hand every week &mdash; the reporting, the
              correspondence, the document wrangling. Half an hour later you have a straight answer.
            </p>

            <ul className="mt-6 border-t border-gray-700">
              {outcomes.map((outcome) => (
                <li key={outcome} className="border-b border-gray-700 py-3 text-gray-200">
                  {outcome}
                </li>
              ))}
            </ul>

            <p className="mt-6 text-sm text-gray-400">
              Here for the infrastructure side?{' '}
              <a
                href="#aws"
                onClick={jumpTo('aws')}
                className="text-cloud-300 transition-colors duration-150 hover:text-cloud-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-400"
              >
                The AWS practice has its own free assessment
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default HeroSection;
